export interface StoredCollectionSessionV2 {
  readonly schema_version: 2;
  readonly revision: number;
  readonly collection_id: string;
  readonly access_token: string;
  readonly session_id: string;
  readonly expires_at_epoch_ms: number;
}

export interface CollectionSessionCredentials {
  readonly access_token: string;
  readonly session_id: string;
  readonly expires_at_epoch_ms: number;
}

interface StoredCollectionSessionTombstoneV2 {
  readonly schema_version: 2;
  readonly revision: number;
  readonly collection_id: string;
  readonly state: 'CLEARED';
}

export interface CollectionSessionStorageState {
  readonly revision: number | null;
  readonly session: StoredCollectionSessionV2 | null;
}

type CollectionSessionStoragePort = Pick<Storage, 'getItem' | 'setItem'>;
type LegacyCollectionSessionPurgeStoragePort = Pick<
  Storage,
  'key' | 'length' | 'removeItem'
>;

const SESSION_SCHEMA_VERSION = 2 as const;
const SESSION_KEY_PREFIX = 'mzic_collection_session_v2';
const LEGACY_SESSION_KEY_PREFIX = 'mzic_collection_token_';
const LEGACY_LAST_COLLECTION_KEY = 'mzic_collection_last_id';
const SESSION_KEYS = new Set([
  'schema_version',
  'revision',
  'collection_id',
  'access_token',
  'session_id',
  'expires_at_epoch_ms',
]);
const TOMBSTONE_KEYS = new Set([
  'schema_version',
  'revision',
  'collection_id',
  'state',
]);

export function purgeLegacyCollectionSessionCredentials(
  storage: LegacyCollectionSessionPurgeStoragePort,
): void {
  for (let index = storage.length - 1; index >= 0; index -= 1) {
    const key = storage.key(index);
    if (
      key === LEGACY_LAST_COLLECTION_KEY ||
      key?.startsWith(LEGACY_SESSION_KEY_PREFIX)
    ) {
      storage.removeItem(key);
    }
  }
}

export class CollectionSessionStorage {
  constructor(private readonly storage: CollectionSessionStoragePort) {}

  keyFor(collectionId: string): string {
    const normalizedCollectionId = requireNonEmptyString(
      collectionId,
      'collectionId',
    );
    return `${SESSION_KEY_PREFIX}:${normalizedCollectionId}`;
  }

  read(collectionId: string): StoredCollectionSessionV2 | null {
    return this.readState(collectionId).session;
  }

  readState(collectionId: string): CollectionSessionStorageState {
    const normalizedCollectionId = requireNonEmptyString(
      collectionId,
      'collectionId',
    );
    const raw = this.storage.getItem(this.keyFor(normalizedCollectionId));
    if (raw === null) return EMPTY_STORAGE_STATE;

    try {
      const candidate: unknown = JSON.parse(raw);
      if (isStoredCollectionSession(candidate, normalizedCollectionId)) {
        const session = Object.freeze({ ...candidate });
        return Object.freeze({ revision: session.revision, session });
      }
      if (isSessionTombstone(candidate, normalizedCollectionId)) {
        return Object.freeze({ revision: candidate.revision, session: null });
      }
      return EMPTY_STORAGE_STATE;
    } catch {
      return EMPTY_STORAGE_STATE;
    }
  }

  compareAndSet(
    collectionId: string,
    expectedRevision: number | null,
    credentials: CollectionSessionCredentials,
  ): StoredCollectionSessionV2 | null {
    const normalizedCollectionId = requireNonEmptyString(
      collectionId,
      'collectionId',
    );
    validateExpectedRevision(expectedRevision);
    validateCredentials(credentials);

    const current = this.readState(normalizedCollectionId);
    if (current.revision !== expectedRevision) return null;

    const next: StoredCollectionSessionV2 = Object.freeze({
      schema_version: SESSION_SCHEMA_VERSION,
      revision: (current.revision ?? 0) + 1,
      collection_id: normalizedCollectionId,
      access_token: credentials.access_token.trim(),
      session_id: credentials.session_id.trim(),
      expires_at_epoch_ms: credentials.expires_at_epoch_ms,
    });
    this.storage.setItem(
      this.keyFor(normalizedCollectionId),
      JSON.stringify(next),
    );
    return next;
  }

  compareAndClear(collectionId: string, expectedRevision: number): boolean {
    const normalizedCollectionId = requireNonEmptyString(
      collectionId,
      'collectionId',
    );
    validateExpectedRevision(expectedRevision);
    const current = this.readState(normalizedCollectionId);
    if (current.revision !== expectedRevision || current.session === null) {
      return false;
    }

    const tombstone: StoredCollectionSessionTombstoneV2 = Object.freeze({
      schema_version: SESSION_SCHEMA_VERSION,
      revision: expectedRevision + 1,
      collection_id: normalizedCollectionId,
      state: 'CLEARED',
    });
    this.storage.setItem(
      this.keyFor(normalizedCollectionId),
      JSON.stringify(tombstone),
    );
    return true;
  }
}

const EMPTY_STORAGE_STATE: CollectionSessionStorageState = Object.freeze({
  revision: null,
  session: null,
});

function isStoredCollectionSession(
  candidate: unknown,
  collectionId: string,
): candidate is StoredCollectionSessionV2 {
  if (!isRecord(candidate)) return false;
  if (
    Object.keys(candidate).length !== SESSION_KEYS.size ||
    Object.keys(candidate).some((key) => !SESSION_KEYS.has(key))
  ) {
    return false;
  }

  return (
    candidate['schema_version'] === SESSION_SCHEMA_VERSION &&
    isPositiveInteger(candidate['revision']) &&
    candidate['collection_id'] === collectionId &&
    isNonEmptyString(candidate['access_token']) &&
    isNonEmptyString(candidate['session_id']) &&
    isPositiveInteger(candidate['expires_at_epoch_ms'])
  );
}

function isSessionTombstone(
  candidate: unknown,
  collectionId: string,
): candidate is StoredCollectionSessionTombstoneV2 {
  if (!isRecord(candidate)) return false;
  if (
    Object.keys(candidate).length !== TOMBSTONE_KEYS.size ||
    Object.keys(candidate).some((key) => !TOMBSTONE_KEYS.has(key))
  ) {
    return false;
  }
  return (
    candidate['schema_version'] === SESSION_SCHEMA_VERSION &&
    isPositiveInteger(candidate['revision']) &&
    candidate['collection_id'] === collectionId &&
    candidate['state'] === 'CLEARED'
  );
}

function validateCredentials(credentials: CollectionSessionCredentials): void {
  if (!isNonEmptyString(credentials.access_token)) {
    throw new TypeError('access_token must be a non-empty string');
  }
  if (!isNonEmptyString(credentials.session_id)) {
    throw new TypeError('session_id must be a non-empty string');
  }
  if (!isPositiveInteger(credentials.expires_at_epoch_ms)) {
    throw new TypeError('expires_at_epoch_ms must be a positive integer');
  }
}

function validateExpectedRevision(revision: number | null): void {
  if (revision !== null && !isPositiveInteger(revision)) {
    throw new TypeError('expectedRevision must be null or a positive integer');
  }
}

function requireNonEmptyString(value: string, field: string): string {
  if (!isNonEmptyString(value)) {
    throw new TypeError(`${field} must be a non-empty string`);
  }
  return value.trim();
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function isPositiveInteger(value: unknown): value is number {
  return Number.isInteger(value) && Number.isFinite(value) && Number(value) > 0;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
